import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SITE_INFO } from '../data/siteData';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Main menu items
  const primaryLinks = [
    { label: "Hjem", href: "/" },
    { label: "Forløb", href: "/vores-tjenester" },
    { label: "Om KerneViden", href: "/om-os" },
    { label: "Blogindlæg", href: "/blogindlaeg" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-white border-b border-[#EFEAE2] transition-shadow duration-200 ${
        scrolled ? 'shadow-sm' : 'shadow-none'
      }`}
    >
      {/* Top row with centered logo (and side CTA / contact info on desktop) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between min-[1200px]:grid min-[1200px]:grid-cols-3 py-3.5 md:py-4">
          
          {/* Desktop Left: Contact info */}
          <div className="hidden min-[1200px]:flex items-center">
            <a
              href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
              className="text-sm font-medium text-black inline-flex items-center gap-2.5 transition-colors whitespace-nowrap hover:opacity-75"
            >
              <i className="fa-regular fa-phone text-black text-sm" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium text-black">{SITE_INFO.phone}</span>
            </a>
          </div>

          {/* Centered Logo */}
          <div className="flex items-center justify-start min-[1200px]:justify-center">
            <Link
              to="/"
              className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-[#3A4F3D] rounded-xl p-1 transition-transform"
              aria-label="KerneViden - Til forsiden"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,format=auto/assets/1d30bca3-a748-4ede-8335-828014d68a29/logo.png"
                alt="KerneViden Logo"
                className="h-16 w-auto sm:h-20 md:h-24 object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Right: CTA Button */}
          <div className="hidden min-[1200px]:flex items-center justify-end gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-medium text-white bg-[#3A4F3D] hover:bg-[#2C3E2D] shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer whitespace-nowrap group"
            >
              <span className="whitespace-nowrap">Gratis indledende samtale</span>
              <i className="fa-solid fa-arrow-right ml-2 text-xs text-white transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile / Tablet Hamburger Button (only visible < 1200px) */}
          <div className="flex items-center gap-2 min-[1200px]:hidden">
            <Link
              to="/kontakt"
              className="hidden sm:inline-flex items-center justify-center px-3.5 py-2 rounded-full text-xs font-medium text-white bg-[#3A4F3D] hover:bg-[#2C3E2D] transition-colors whitespace-nowrap mr-1 gap-1.5"
            >
              <span>Gratis samtale</span>
              <i className="fa-solid fa-arrow-right text-[10px] text-white" aria-hidden="true" />
            </Link>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-xl text-black transition-colors focus:outline-none cursor-pointer"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i
                className={`fa-solid text-xl text-black ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Centered Desktop Navigation Bar directly underneath the logo (only >= 1200px) */}
      <div className="hidden min-[1200px]:block border-t border-[#EFEAE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-center gap-1 xl:gap-2 py-2" aria-label="Hovednavigation">
            {primaryLinks.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === "/"}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-[15px] transition-colors duration-200 ${
                    isActive
                      ? 'text-[#233424] font-semibold'
                      : 'text-[#566555] hover:text-[#233424]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile / Tablet Menu Drawer (under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-[#EFEAE2] shadow-xl px-4 pt-3 pb-8 transition-all animate-in slide-in-from-top duration-200">
          <div className="flex flex-col space-y-1 divide-y divide-[#F0EBE3]">
            <div className="flex flex-col space-y-1 pb-3">
              {primaryLinks.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-medium transition-colors flex items-center justify-between ${
                      isActive
                        ? 'bg-[#F2F2F2] text-black font-semibold'
                        : 'text-[#3E4A3E] hover:bg-[#F8F8F8] hover:text-black'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <i className="fa-regular fa-angle-right text-xs text-[#8A9786]" aria-hidden="true" />
                </NavLink>
              ))}
            </div>

            {/* Mobile Contact & CTA block */}
            <div className="pt-4 space-y-3">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center py-3.5 px-4 rounded-xl text-base font-medium text-white bg-[#3A4F3D] hover:bg-[#2C3E2D] shadow-xs text-center transition-colors cursor-pointer"
              >
                <span className="whitespace-nowrap">Book gratis indledende samtale</span>
              </Link>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-sm text-[#4E5C4E]">
                <a
                  href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-2.5 py-1.5 px-0 text-black hover:opacity-75 transition-opacity"
                >
                  <i className="fa-regular fa-phone text-black text-sm" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium text-black">{SITE_INFO.phoneFormatted}</span>
                </a>
                <a
                  href={`mailto:${SITE_INFO.email}`}
                  className="flex items-center gap-2.5 py-1.5 px-0 text-black hover:opacity-75 transition-opacity"
                >
                  <i className="fa-regular fa-envelope text-black text-sm" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium text-black truncate">{SITE_INFO.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
